/**
 * AI Truth tab — confirmation strip + FAQ answers editor.
 *
 * - Auto-activates the AI Truth endpoint on mount (idempotent, fire-and-forget).
 * - Gate: if schemaDeliveryMode is 'undecided', prompt the customer to complete
 *   schema delivery setup first (it was chosen during onboarding).
 * - Active state: delivery confirmation strip + AI-suggested Q&A editor.
 *   Customers read each AI-suggested answer, edit if needed, then approve or reject.
 *   Approved answers are included in the injected schema automatically — no extra steps.
 */

import { useState, useEffect, useCallback } from 'react';
import { api } from '../../api';
import type { AiTruthAnswer, DomainDetail, SchemaDeliveryMode } from '../../types';

// ── Helpers ───────────────────────────────────────────────────────────────────

function deliveryLabel(mode: SchemaDeliveryMode): string {
  switch (mode) {
    case 'wordpress_plugin': return 'WordPress plugin';
    case 'snippet':          return 'code snippet';
    case 'edge_proxy':       return 'Cloudflare edge proxy';
    case 'beacon_js':        return 'JS beacon';
    default:                 return 'delivery method';
  }
}

// ── Sub-components ────────────────────────────────────────────────────────────

function GateCard({ onSettings }: { onSettings: () => void }) {
  return (
    <div className="max-w-lg mx-auto mt-16 text-center">
      <div className="text-4xl mb-4">🔗</div>
      <h2 className="text-lg font-semibold text-gray-800 mb-2">Schema delivery not set up</h2>
      <p className="text-sm text-gray-500 mb-6 leading-relaxed">
        AI Truth data is injected via your chosen delivery method (WordPress plugin, code snippet,
        or Cloudflare edge proxy). Complete your schema delivery setup first — then AI Truth
        answers will be included automatically.
      </p>
      <button
        onClick={onSettings}
        className="inline-flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium px-5 py-2.5 rounded-lg transition-colors"
      >
        Go to Settings → Schema Delivery
      </button>
    </div>
  );
}

function EntitlementCard() {
  return (
    <div className="max-w-lg mx-auto mt-16 text-center">
      <div className="text-4xl mb-4">🔒</div>
      <h2 className="text-lg font-semibold text-gray-800 mb-2">AI Truth not included in your plan</h2>
      <p className="text-sm text-gray-500 mb-6">Upgrade to Growth or Scale to unlock AI Truth Q&amp;A injection.</p>
      <a
        href="/billing"
        className="inline-flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium px-5 py-2.5 rounded-lg transition-colors"
      >
        View plans
      </a>
    </div>
  );
}

function DeliveryStrip({ mode }: { mode: SchemaDeliveryMode }) {
  return (
    <div className="flex items-center gap-3 bg-green-50 border border-green-200 rounded-lg px-4 py-3 mb-6">
      <span className="text-green-600 text-lg">✓</span>
      <div>
        <p className="text-sm font-medium text-green-800">
          AI Truth data is included in your {deliveryLabel(mode)}
        </p>
        <p className="text-xs text-green-600 mt-0.5">
          Approved answers below are injected automatically — no extra installation needed.
        </p>
      </div>
    </div>
  );
}

interface DraftCardProps {
  answer: AiTruthAnswer;
  onApprove: (id: string, text: string) => Promise<void>;
  onReject: (id: string) => Promise<void>;
}

function DraftCard({ answer, onApprove, onReject }: DraftCardProps) {
  const [text, setText] = useState(answer.answerText);
  const [busy, setBusy] = useState(false);

  async function handleApprove() {
    setBusy(true);
    try {
      await onApprove(answer.id, text);
    } finally {
      setBusy(false);
    }
  }

  async function handleReject() {
    setBusy(true);
    try {
      await onReject(answer.id);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="border border-amber-200 bg-amber-50 rounded-lg p-4">
      <div className="flex items-start justify-between gap-2 mb-2">
        <p className="text-sm font-medium text-gray-800">{answer.questionText ?? 'Question'}</p>
        <span className="shrink-0 text-xs bg-amber-100 text-amber-700 border border-amber-200 rounded px-2 py-0.5">
          AI draft
        </span>
      </div>
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={4}
        className="w-full text-sm text-gray-700 border border-gray-200 rounded-md px-3 py-2 bg-white resize-y focus:outline-none focus:ring-2 focus:ring-indigo-400"
        placeholder="Edit the AI-suggested answer before approving…"
        disabled={busy}
      />
      <div className="flex gap-2 mt-2">
        <button
          onClick={handleApprove}
          disabled={busy || text.trim().length === 0}
          className="flex-1 text-sm font-medium bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white rounded-md py-1.5 transition-colors"
        >
          {busy ? 'Saving…' : 'Approve'}
        </button>
        <button
          onClick={handleReject}
          disabled={busy}
          className="text-sm font-medium text-gray-500 hover:text-red-600 border border-gray-200 hover:border-red-200 rounded-md px-4 py-1.5 transition-colors"
        >
          Reject
        </button>
      </div>
    </div>
  );
}

interface ApprovedCardProps {
  answer: AiTruthAnswer;
  onEdit: (id: string, text: string) => Promise<void>;
}

function ApprovedCard({ answer, onEdit }: ApprovedCardProps) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(answer.answerText);
  const [busy, setBusy] = useState(false);

  async function handleSave() {
    setBusy(true);
    try {
      await onEdit(answer.id, text);
      setEditing(false);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="border border-green-200 bg-white rounded-lg p-4">
      <div className="flex items-start justify-between gap-2 mb-1">
        <p className="text-sm font-medium text-gray-800">{answer.questionText ?? 'Question'}</p>
        <span className="shrink-0 text-xs bg-green-100 text-green-700 border border-green-200 rounded px-2 py-0.5">
          approved
        </span>
      </div>
      {editing ? (
        <>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={4}
            className="w-full text-sm text-gray-700 border border-gray-200 rounded-md px-3 py-2 bg-white resize-y focus:outline-none focus:ring-2 focus:ring-indigo-400 mt-2"
            disabled={busy}
          />
          <div className="flex gap-2 mt-2">
            <button
              onClick={handleSave}
              disabled={busy || text.trim().length === 0}
              className="flex-1 text-sm font-medium bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white rounded-md py-1.5 transition-colors"
            >
              {busy ? 'Saving…' : 'Save'}
            </button>
            <button
              onClick={() => { setEditing(false); setText(answer.answerText); }}
              disabled={busy}
              className="text-sm font-medium text-gray-500 hover:text-gray-700 border border-gray-200 rounded-md px-4 py-1.5 transition-colors"
            >
              Cancel
            </button>
          </div>
        </>
      ) : (
        <>
          <p className="text-sm text-gray-600 mt-1 whitespace-pre-wrap">{answer.answerText}</p>
          <button
            onClick={() => setEditing(true)}
            className="mt-2 text-xs text-indigo-600 hover:text-indigo-800 font-medium"
          >
            Edit
          </button>
        </>
      )}
    </div>
  );
}

function RejectedCard({ answer, onRestore }: { answer: AiTruthAnswer; onRestore: (id: string) => void }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-gray-100 bg-gray-50 rounded-lg p-3">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center justify-between w-full text-left"
      >
        <p className="text-sm text-gray-400 line-through">{answer.questionText ?? 'Question'}</p>
        <span className="text-xs text-gray-400 ml-2">{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <div className="mt-2">
          <p className="text-xs text-gray-400 italic">{answer.answerText}</p>
          <button
            onClick={() => onRestore(answer.id)}
            className="mt-2 text-xs text-indigo-500 hover:text-indigo-700 font-medium"
          >
            Restore as draft
          </button>
        </div>
      )}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

interface Props {
  domain: DomainDetail;
  aiTruthEnabled: boolean;
  tier: string;
  onBack: () => void;
}

type UIState =
  | { kind: 'loading' }
  | { kind: 'gate' }
  | { kind: 'entitlement' }
  | { kind: 'active'; answers: AiTruthAnswer[] };

export function AiTruthWizard({ domain, aiTruthEnabled, onBack }: Props) {
  const [state, setState] = useState<UIState>({ kind: 'loading' });
  const [loadError, setLoadError] = useState<string | null>(null);

  const loadAnswers = useCallback(async () => {
    try {
      const answers = await api.aiTruth.listAnswers(domain.id);
      setState({ kind: 'active', answers });
    } catch {
      setLoadError('Failed to load answers. Please refresh.');
    }
  }, [domain.id]);

  useEffect(() => {
    if (!aiTruthEnabled) {
      setState({ kind: 'entitlement' });
      return;
    }
    if (domain.schemaDeliveryMode === 'undecided') {
      setState({ kind: 'gate' });
      return;
    }

    // Auto-activate (idempotent — safe to call every mount).
    void api.aiTruth.activate(domain.id).catch(() => {
      // Silently ignore — endpoint may already be active.
    });

    void loadAnswers();
  }, [domain.id, domain.schemaDeliveryMode, aiTruthEnabled, loadAnswers]);

  // ── Handlers ─────────────────────────────────────────────────────────────

  function mergeQText(updated: AiTruthAnswer, original: AiTruthAnswer): AiTruthAnswer {
    return original.questionText !== undefined
      ? { ...updated, questionText: original.questionText }
      : updated;
  }

  async function handleApprove(answerId: string, text: string) {
    const updated = await api.aiTruth.approveAnswer(domain.id, answerId, text);
    setState((prev) => {
      if (prev.kind !== 'active') return prev;
      return {
        kind: 'active',
        answers: prev.answers.map((a) => (a.id === answerId ? mergeQText(updated, a) : a)),
      };
    });
  }

  async function handleReject(answerId: string) {
    const updated = await api.aiTruth.rejectAnswer(domain.id, answerId);
    setState((prev) => {
      if (prev.kind !== 'active') return prev;
      return {
        kind: 'active',
        answers: prev.answers.map((a) => (a.id === answerId ? mergeQText(updated, a) : a)),
      };
    });
  }

  async function handleEdit(answerId: string, text: string) {
    const result = await api.aiTruth.patchAnswer(domain.id, answerId, text);
    setState((prev) => {
      if (prev.kind !== 'active') return prev;
      const original = prev.answers.find((a) => a.id === answerId);
      const next = original !== undefined ? mergeQText(result.next, original) : result.next;
      return {
        kind: 'active',
        answers: prev.answers.filter((a) => a.id !== answerId).concat(next),
      };
    });
  }

  function handleRestoreRejected(_answerId: string) {
    void loadAnswers();
  }

  // ── Render ────────────────────────────────────────────────────────────────

  if (state.kind === 'loading') {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="w-6 h-6 border-2 border-indigo-400 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (state.kind === 'entitlement') {
    return <EntitlementCard />;
  }

  if (state.kind === 'gate') {
    return (
      <GateCard
        onSettings={() => {
          // Navigate back; user goes to Settings > Schema Delivery.
          onBack();
        }}
      />
    );
  }

  if (loadError) {
    return (
      <div className="max-w-lg mx-auto mt-16 text-center">
        <p className="text-sm text-red-500">{loadError}</p>
        <button
          onClick={() => { setLoadError(null); setState({ kind: 'loading' }); void loadAnswers(); }}
          className="mt-3 text-sm text-indigo-600 hover:underline"
        >
          Try again
        </button>
      </div>
    );
  }

  const { answers } = state;
  const drafts   = answers.filter((a) => a.status === 'draft');
  const approved = answers.filter((a) => a.status === 'approved' && a.supersededBy === null);
  const rejected = answers.filter((a) => a.status === 'rejected');

  return (
    <div className="max-w-2xl mx-auto py-6 px-4">
      <DeliveryStrip mode={domain.schemaDeliveryMode} />

      {answers.length === 0 && (
        <div className="text-center py-12 text-gray-400 text-sm">
          <p className="font-medium mb-1">No Q&amp;A pairs generated yet</p>
          <p className="text-xs">Run an audit to generate AI-suggested answers about your business.</p>
        </div>
      )}

      {drafts.length > 0 && (
        <section className="mb-6">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">
            Review AI suggestions ({drafts.length})
          </h3>
          <div className="space-y-3">
            {drafts.map((a) => (
              <DraftCard
                key={a.id}
                answer={a}
                onApprove={handleApprove}
                onReject={handleReject}
              />
            ))}
          </div>
        </section>
      )}

      {approved.length > 0 && (
        <section className="mb-6">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">
            Live answers ({approved.length})
          </h3>
          <div className="space-y-3">
            {approved.map((a) => (
              <ApprovedCard key={a.id} answer={a} onEdit={handleEdit} />
            ))}
          </div>
        </section>
      )}

      {rejected.length > 0 && (
        <section>
          <h3 className="text-sm font-semibold text-gray-400 mb-2 text-xs uppercase tracking-wide">
            Rejected ({rejected.length})
          </h3>
          <div className="space-y-2">
            {rejected.map((a) => (
              <RejectedCard key={a.id} answer={a} onRestore={handleRestoreRejected} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
